'use client';

// Note that environment variables cannot be destructured for client-side usage
// due to the way Next.js handles environment variable exposure. Client-side
// environment variables must be explicitly accessed through `process.env`
// with the appropriate `NEXT_PUBLIC_` prefix.
import { AnalyticsProps } from '@/types';
import { getReferralId, getReferralIdFromQuery } from '@/utils/referral';
import { useEffect } from 'react';

const FIRSTPROMOTER_PUBLIC_KEY =
  process.env.NEXT_PUBLIC_FIRSTPROMOTER_PUBLIC_KEY;

const FIRSTPROMOTER_PUBLIC_DOMAIN =
  process.env.NEXT_PUBLIC_FIRSTPROMOTER_PUBLIC_DOMAIN;

const isFirstpromoterEnabled = Boolean(
  FIRSTPROMOTER_PUBLIC_KEY && FIRSTPROMOTER_PUBLIC_DOMAIN
);

type Promoter = {
  id: string;
  first_name: string;
  last_name: string;
};

type FirstPromoterData = {
  ref_id: string;
  promo_code: string;
  campaign_id: string;
  customer_promo_code: string;
  offer: string;
  promoter: Promoter;
};

declare global {
  interface Window {
    fpr: (method: string, payload: any, callback?: any) => void;
  }
}

function reportReferralViaLambda(email: string, referralId: string) {
  try {
    return fetch(
      `https://lnwtlnlauc.execute-api.us-east-1.amazonaws.com/dev/track/signup?email=${encodeURI(email)}&ref_id=${encodeURI(referralId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        mode: 'no-cors'
      }
    );
  } catch (error) {
    console.error('Failed to report referral via lambda due to ', error);
  }
}

async function reportReferralViaFirstPromoterSDK(email: string) {
  try {
    if (typeof window !== 'undefined' && window.fpr) {
      return new Promise<void>((resolve, reject) => {
        let isFulfilled = false;
        window.fpr('onReady', () => {
          window.fpr('referral', { email }, (res: any) => {
            if (res.errors) {
              isFulfilled = true;
              reject(res.errors[0]);
            } else {
              isFulfilled = true;
              resolve();
            }
          });
        });
        setTimeout(() => {
          if (!isFulfilled) {
            reject('Timeout while reporting referral');
          }
        }, 2_000);
      });
    }
  } catch (error) {
    console.error(
      'Failed to report referral via first promoter sdk due to ',
      error
    );
  }
}

export async function trackSignup(
  email: string,
  analyticsProps?: AnalyticsProps
) {
  try {
    const ref_id = getReferralId();
    const via_val = getReferralIdFromQuery();
    if (ref_id) {
      const response = await Promise.allSettled([
        reportReferralViaLambda(email, ref_id),
        reportReferralViaFirstPromoterSDK(email)
      ]);

      const isSuccess = response.find((r) => r.status === 'fulfilled');

      if (isSuccess) {
        rudderanalytics.track('FirstPromoter Signup', {
          parent_id: analyticsProps?.analyticsParentId ?? 'Campaign Page',
          element_id:
            analyticsProps?.analyticsElementId ?? 'campaign-submit-button',
          email,
          ref_id,
          via_val,
          context: {
            ...(analyticsProps?.analyticsContext ?? {}),
            email,
            ref_id,
            via_val
          }
        });
      }
    }
  } catch (e: any) {
    console.error(`Error while tracking signup due to: ${e}`);
  }
}

export function trackClick() {
  setTimeout(async () => {
    try {
      const ref_id = getReferralIdFromQuery();

      if (ref_id && typeof window !== 'undefined' && window.fpr) {
        window.fpr('onReady', () => {
          window.fpr('click', { ref_id });
        });
      }
    } catch (e: any) {}
  });
}

export function InitializeFirstPromoter() {
  useEffect(() => {
    trackClick();
  }, []);

  return null;
}

export const FirstPromoterScript = `
  (function(w) {
    w.fpr=w.fpr||function(){
      w.fpr.q = w.fpr.q || [];
      w.fpr.q[arguments[0]=='set'?'unshift':'push'](arguments);
    };

    if (${isFirstpromoterEnabled}) {
      var script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = 'https://cdn.firstpromoter.com/fpr.js';
      var firstScript = document.getElementsByTagName("script")[0];
      firstScript.parentNode.insertBefore(script, firstScript);

      fpr("init", {cid:"${FIRSTPROMOTER_PUBLIC_KEY}", domain: "${FIRSTPROMOTER_PUBLIC_DOMAIN}"});
    }
  })(window);
`;
